// Шапка и подвал сайта — ОДНИ на все страницы (решение владельца 06.10.2026).
//
// Перенесены с новой главной (design/reference/Home*.dc.html) как есть. До
// этого на главной стояла новая шапка, а на карте профессий, «О нас»,
// тарифах, методологии, коучах и юридических страницах — старая, со ссылками
// «Как это работает» и «Пример отчёта», уводившими на главную.
//
// Где живёт:
//   · React-страницы (главная, карта и её подстраницы, коучи) рисуют
//     <SiteHeader>/<SiteFooter> сами;
//   · статические HTML (о нас, тарифы, методология, политика, условия) —
//     сервер вставляет на место <!--site-header--> / <!--site-footer--> ту же
//     разметку через renderToStaticMarkup (src/components/site/site-chrome-html.ts).
//
// Поведение (выпадающие меню, мобильное меню, кука языка) — одна функция
// initSiteHeader без состояния React: так она одинаково работает и в
// React-странице, и встроенным скриптом в статической. Поэтому она обязана
// быть самодостаточной — без ссылок на что-либо вне своего тела.
import { YolutapLockup } from "@/components/brand/YolutapLogo";
import { BLOG_COPY } from "@/components/blog/blog-copy";
import { BLOG_PATH } from "@/lib/blog";
import { FREE_TEST_LABEL, FREE_TEST_PATH } from "@/components/free-test/free-test-copy";
import { SCHOOLS_PATH } from "@/components/schools/schools-copy";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { hasTutorFlag } from "@/lib/tutor-flag";

import ru from "../../../design/content/ru.json";
import az from "../../../design/content/az.json";
import en from "../../../design/content/en.json";
import kept from "../home/kept-copy.json";

export type SiteLang = "ru" | "az" | "en";
/** Раздел, который подсвечивается в шапке (или в подвале — для юридических). */
export type SiteSection = "careers" | "coaches" | "abroad" | "about" | "methodology" | "pricing" | "privacy" | "terms" | "tutors" | "schools";

type Copy = typeof ru;
const COPY: Record<SiteLang, Copy> = { ru, az: az as Copy, en: en as Copy };

const PRE: Record<SiteLang, string> = { ru: "", az: "/az", en: "/en" };
export const HOME_PATH: Record<SiteLang, string> = { ru: "/ru", az: "/az", en: "/en" };
const METHOD_PATH: Record<SiteLang, string> = { ru: "/metodologiya", az: "/az/metodologiya", en: "/en/methodology" };
/** Образец отчёта — тот же, что на главной (страница «Школам» берёт отсюда). */
export const SAMPLE_PDF: Record<SiteLang, string> = {
  ru: "/samples/sample-16-18-ru-leader-communicator-v1.pdf",
  az: "/sample-report.pdf",
  en: "/samples/sample-18-plus-en-investigative-social-artistic-v1.pdf",
};
const TERMS: Record<SiteLang, string> = { ru: "Условия использования", az: "İstifadə Şərtləri", en: "Terms of Use" };

/** Куда ведёт «Учёба за рубежом» — одно место на весь сайт: лендинг модуля. */
export const STUDY_ABROAD_HREF: Record<SiteLang, string> = {
  ru: "/study-abroad",
  az: "/az/xaricde-tehsil",
  en: "/en/study-abroad",
};
/** Карта репетиторов и кружков для родителей — пункт «Семьям» в меню. */
export const TUTORS_MAP_HREF: Record<SiteLang, string> = { ru: "/ru/tutors", az: "/az/tutors", en: "/en/tutors" };
// Новые подписи — на утверждении владельца, AZ — на вычитку.
const TUTORS_MAP_ITEM: Record<SiteLang, { title: string; desc: string }> = {
  ru: { title: "Репетиторы и кружки", desc: "Карта рядом с домом" },
  az: { title: "Repetitorlar və dərnəklər", desc: "Evə yaxın xəritə" },
  en: { title: "Tutors and clubs", desc: "A map near home" },
};
/** Страница регистрации репетиторов и кружков: пилюля в шапке и ссылка в подвале. */
export const TUTORS_JOIN_HREF: Record<SiteLang, string> = { ru: "/ru/tutors/join", az: "/az/tutors/join", en: "/en/tutors/join" };
// Подвал — прежняя подпись. AZ — на вычитку носителем.
const TUTORS_JOIN_LABEL: Record<SiteLang, string> = {
  ru: "Для репетиторов и кружков",
  az: "Repetitorlar və dərnəklər üçün",
  en: "For tutors and clubs",
};
/**
 * Короткая подпись «Yolutap haqqında» — только в строке шапки на 1280–1439,
 * где полная не помещается (решение владельца по PR #384). Меню, подвал и
 * общий ключ about_yolutap не трогаем.
 */
export const HEADER_ABOUT_SHORT: Record<SiteLang, string> = {
  az: "Haqqımızda",
  ru: "О нас",
  en: "About",
};
/** Пилюля шапки — на всех страницах (ТЗ «два пути», 09.10.2026); на телефоне (<690) — в бургере. */
/** Кабинет репетитора карты (24.10.2026): кнопка вошедшего и пункт меню «Вход». */
export const TUTORS_ME_HREF: Record<SiteLang, string> = { ru: "/ru/tutors/me", az: "/az/tutors/me", en: "/en/tutors/me" };
export const MY_CABINET_LABEL: Record<SiteLang, string> = { ru: "Мой кабинет", az: "Kabinetim", en: "My dashboard" };
const TUTOR_CABINET_ITEM: Record<SiteLang, { title: string; desc: string }> = {
  ru: { title: "Кабинет репетитора", desc: "Анкета на карте «Рядом с домом»" },
  az: { title: "Repetitor kabineti", desc: "«Evə yaxın» xəritəsindəki anket" },
  en: { title: "Tutor dashboard", desc: "Your profile on the \"Near home\" map" },
};

export const TUTORS_SIGNUP_LABEL: Record<SiteLang, string> = {
  ru: "Регистрация репетитора",
  az: "Repetitor qeydiyyatı",
  en: "Tutor sign-up",
};

/** «Giriş» на телефоне ≤ 480 (ТЗ владельца 25.10.2026). AZ — на вычитку носителем. */
const PHONE_LOGIN: Record<SiteLang, { parent: string; tutor: string; school: string }> = {
  az: { parent: "Valideyn / Şagird", tutor: "Repetitor", school: "Müəllim kabineti" },
  ru: { parent: "Родитель / Ученик", tutor: "Репетитор", school: "Кабинет учителя" },
  en: { parent: "Parent / Student", tutor: "Tutor", school: "Teacher login" },
};

/** Порядок языков в переключателе — везде один. */
export const SITE_LANGS: readonly SiteLang[] = ["az", "ru", "en"];
/** Название языка на нём самом — в выпадающем меню компактного переключателя. */
const LANG_NAME: Record<SiteLang, string> = { az: "Azərbaycanca", ru: "Русский", en: "English" };
/**
 * Подпись переключателя для экранного диктора — на трёх языках сразу (кто не
 * читает текущий язык, всё равно узнаёт кнопку). У компактной кнопки к ней
 * добавляется видимый текст («AZ»), чтобы имя совпадало с экраном.
 */
export const LANG_SWITCH_LABEL = "Dil / Язык / Language";

/**
 * Ссылки переключателя языка — ОДНА функция для всех четырёх мест: строка
 * шапки (≥860), компактный переключатель (<860), первая строка бургер-меню и
 * подвал. Куда ведёт — решает страница через langHref (та же страница на
 * другом языке: /az/... ↔ /... ↔ /en/...); по умолчанию — главные.
 */
export function siteLangLinks(
  lang: SiteLang,
  langHref: Record<SiteLang, string> = HOME_PATH,
): { lang: SiteLang; href: string; current: boolean }[] {
  return SITE_LANGS.map((l) => ({ lang: l, href: langHref[l], current: l === lang }));
}

const Chevron = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

type NavItem = { title: string; desc: string; href?: string; soon?: boolean; section?: SiteSection };

/**
 * Поведение шапки. САМОДОСТАТОЧНА: статические страницы получают её
 * встроенным скриптом через Function.prototype.toString. Возвращает отписку.
 */
export function initSiteHeader(root: HTMLElement | null): () => void {
  if (!root || root.getAttribute("data-ready") === "1") return function () {};
  root.setAttribute("data-ready", "1");
  const header = root;
  const burger = header.querySelector<HTMLElement>("[data-burger]");
  const menu = header.querySelector<HTMLElement>("[data-menu]");
  function setMenu(open: boolean) {
    if (!menu || !burger) return;
    menu.hidden = !open;
    burger.setAttribute("aria-expanded", open ? "true" : "false");
  }
  function closeDropdowns(except: string | null) {
    const btns = header.querySelectorAll<HTMLElement>("[data-dd]");
    for (let i = 0; i < btns.length; i++) {
      const id = btns[i].getAttribute("data-dd");
      if (id === except) continue;
      btns[i].setAttribute("aria-expanded", "false");
      const panel = header.querySelector<HTMLElement>('[data-dd-panel="' + id + '"]');
      if (panel) panel.hidden = true;
    }
  }
  function onClick(e: MouseEvent) {
    const target = e.target as Element | null;
    if (!target || !target.closest) return;
    const btn = target.closest<HTMLElement>("[data-dd]");
    if (btn && header.contains(btn)) {
      const id = btn.getAttribute("data-dd");
      const open = btn.getAttribute("aria-expanded") !== "true";
      closeDropdowns(id);
      if (open) setMenu(false);
      btn.setAttribute("aria-expanded", open ? "true" : "false");
      const panel = header.querySelector<HTMLElement>('[data-dd-panel="' + id + '"]');
      if (panel) panel.hidden = !open;
      return;
    }
    if (burger && burger.contains(target)) {
      closeDropdowns(null);
      // ☰ открывается — «Giriş» закрывается (две панели разом не висят).
      const pl = header.querySelector<HTMLDetailsElement>("details[data-plogin][open]");
      if (pl) pl.open = false;
      setMenu(!!menu && menu.hidden);
      return;
    }
    // Кука языка — и из шапки, и из переключателя в подвале.
    const langLink = target.closest<HTMLElement>("[data-lang]");
    if (langLink && (header.contains(langLink) || langLink.closest("[data-site-footer]"))) {
      document.cookie = "yt-lang=" + langLink.getAttribute("data-lang") + ";path=/;max-age=31536000;samesite=lax";
    }
    // Клик мимо открытой панели (в том числе по пустому месту шапки) или по
    // ссылке в ней закрывает выпадающие меню; ссылка закрывает и бургер.
    if (!target.closest("[data-dd-panel]") || target.closest("a")) closeDropdowns(null);
    if (target.closest("a")) setMenu(false);
    // «Giriş» на телефоне — нативный <details>: клик мимо или по ссылке закрывает.
    const plogin = header.querySelector<HTMLDetailsElement>("details[data-plogin]");
    if (plogin && plogin.open && (!plogin.contains(target) || target.closest("a"))) plogin.open = false;
  }
  function onKey(e: KeyboardEvent) {
    if (e.key !== "Escape") return;
    // Фокус был в открытой панели — вернуть его на её кнопку, из бургер-меню —
    // на бургер: клавиатура не теряется в закрытом меню.
    const active = document.activeElement;
    const inPanel = active && active.closest ? active.closest<HTMLElement>("[data-dd-panel]") : null;
    const returnTo =
      inPanel && header.contains(inPanel)
        ? header.querySelector<HTMLElement>('[data-dd="' + inPanel.getAttribute("data-dd-panel") + '"]')
        : null;
    const inMenu = !!(menu && !menu.hidden && active && menu.contains(active));
    closeDropdowns(null);
    setMenu(false);
    const plogin = header.querySelector<HTMLDetailsElement>("details[data-plogin][open]");
    if (plogin) {
      plogin.open = false;
      if (plogin.contains(active)) plogin.querySelector("summary")?.focus();
    }
    if (returnTo) returnTo.focus();
    else if (inMenu && burger) burger.focus();
  }
  // iOS Safari не шлёт click с «некликабельного» места (пустой div, текст), и
  // «Giriş» оставалась открытой после касания мимо (26.10.2026). pointerdown
  // приходит всегда: касание вне панели закрывает её сразу, касание внутри — нет.
  function onPointerDown(e: PointerEvent) {
    const target = e.target as Element | null;
    if (!target || !target.closest) return;
    const pl = header.querySelector<HTMLDetailsElement>("details[data-plogin][open]");
    if (pl && !pl.contains(target)) pl.open = false;
  }
  document.addEventListener("click", onClick);
  document.addEventListener("keydown", onKey);
  document.addEventListener("pointerdown", onPointerDown, true);
  return function () {
    document.removeEventListener("click", onClick);
    document.removeEventListener("keydown", onKey);
    document.removeEventListener("pointerdown", onPointerDown, true);
    header.removeAttribute("data-ready");
  };
}

export function SiteHeader({
  lang,
  active,
  home = false,
  langHref = HOME_PATH,
}: {
  lang: SiteLang;
  active?: SiteSection;
  /** На главной «Цены» и школьные пункты — якоря её же секций. */
  home?: boolean;
  /** Та же страница на других языках; по умолчанию — главные. */
  langHref?: Record<SiteLang, string>;
}) {
  const t = COPY[lang];
  const k = kept[lang];
  const pre = PRE[lang];
  const start = `/start?lang=${lang}`;
  const ref = useRef<HTMLElement>(null);
  useEffect(() => initSiteHeader(ref.current), []);
  // Вошедший репетитор (флажок yt_tutor, src/lib/tutor-flag.ts): одна кнопка «Мой
  // кабинет» вместо «Регистрация репетитора» и «Вход». Сервер рисует гостя, флажок
  // читается после загрузки.
  const [tutor, setTutor] = useState(false);
  useEffect(() => setTutor(hasTutorFlag(document.cookie)), []);
  const loginItems = [{ href: TUTORS_ME_HREF[lang], ...TUTOR_CABINET_ITEM[lang] }, ...k.login.items];
  // «Giriş» на телефоне: кабинет родителя, кабинет репетитора карты и вход учителя
  // (не «для школ» — так подписана ссылка на страницу «Школам», 01.11.2026).
  const phoneLoginItems = [
    { href: `/cabinet?lang=${lang}`, title: PHONE_LOGIN[lang].parent, cta: "header_phone_login_parent" },
    { href: TUTORS_ME_HREF[lang], title: PHONE_LOGIN[lang].tutor, cta: "header_phone_login_tutor" },
    { href: `/cabinet?role=teacher&lang=${lang}`, title: PHONE_LOGIN[lang].school, cta: "header_phone_login_school" },
  ];

  const families: NavItem[] = [
    { title: t.career_profile, desc: t.test_and_10_page_report_35, href: `${start}&pkg=basic` },
    { title: t.coach_session, desc: t.report_and_60_minutes_with_a_coach_250, href: `${start}&pkg=premium` },
    { title: t.coaches, desc: t.who_runs_the_sessions, href: `${pre}/coaches`, section: "coaches" },
    { title: TUTORS_MAP_ITEM[lang].title, desc: TUTORS_MAP_ITEM[lang].desc, href: TUTORS_MAP_HREF[lang], section: "tutors" },
    { title: t.careers, desc: t.a_map_of_careers, href: `${pre}/careers`, section: "careers" },
  ];
  const schools: NavItem[] = [
    // С 30.10.2026 — страница «Школам», а не форма главной.
    { title: t.career_guidance_for_classes, desc: t.a_profile_for_a_whole_year_group, href: SCHOOLS_PATH[lang], section: "schools" },
    { title: t.school_climate, desc: t.anonymous_climate_survey_8_student, href: SCHOOLS_PATH[lang], section: "schools" },
  ];
  const about: NavItem[] = [
    { title: t.about_us, desc: t.team_and_approach, href: `${pre}/about`, section: "about" },
    { title: t.methodology, desc: t.super_holland_piaget, href: METHOD_PATH[lang], section: "methodology" },
    { title: t.sample_report, desc: t.download_the_pdf_10_pages, href: SAMPLE_PDF[lang] },
  ];
  const pricesHref = home ? "#prices" : `${pre}/pricing`;
  const cur = (s?: SiteSection) => (s && s === active ? ("page" as const) : undefined);
  const freeBadge = <span className="hp-free">{t.free.toLowerCase()}</span>;

  const ddItem = (it: NavItem) =>
    it.soon ? (
      <div key={it.title} className="hp-dd__item" aria-disabled="true">
        <b>
          {it.title} <span className="hp-soon">{t.soon}</span>
        </b>
        <span>{it.desc}</span>
      </div>
    ) : (
      <a key={it.title} className="hp-dd__item" href={it.href} aria-current={cur(it.section)}>
        <b>{it.title}</b>
        <span>{it.desc}</span>
      </a>
    );
  const menuItem = (it: NavItem) =>
    it.soon ? (
      <span key={it.title} className="hp-menu__soon">
        {it.title}
        <span className="hp-soon">{t.soon}</span>
      </span>
    ) : (
      <a key={it.title} href={it.href} aria-current={cur(it.section)}>
        {it.title}
      </a>
    );
  const dropdown = (
    id: string,
    label: ReactNode,
    items: NavItem[],
    width: number,
    cols = 1,
    extra = "",
  ) => (
    <div className={extra ? `hp-dd ${extra}` : "hp-dd"}>
      <button
        type="button"
        className="hp-dd__btn"
        data-dd={id}
        aria-expanded="false"
        data-active={items.some((it) => it.section && it.section === active) ? "" : undefined}
      >
        {label} <Chevron />
      </button>
      <div className="hp-dd__panel" data-dd-panel={id} hidden style={{ width, gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {items.map(ddItem)}
      </div>
    </div>
  );
  const langs = siteLangLinks(lang, langHref);
  const langLinks = () =>
    langs.map((x) => (
      <a key={x.lang} href={x.href} hrefLang={x.lang} data-lang={x.lang} aria-current={x.current ? "true" : undefined}>
        {x.lang.toUpperCase()}
      </a>
    ));

  return (
    <header className={home ? "hp hp-header hp-header--home" : "hp hp-header"} ref={ref} data-site-header="">
      <div className="hp-wrap hp-header__row">
        <a href={HOME_PATH[lang]} className="hp-logo" aria-label={t.yolutap_home}>
          <YolutapLockup />
        </a>
        <nav className="hp-nav" aria-label={t.main_menu}>
          {dropdown("fam", t.families, families, 540, 2, "hp-nav__i--fam")}
          <a
            className="hp-nav__link hp-nav__i--abroad"
            href={STUDY_ABROAD_HREF[lang]}
            aria-current={cur("abroad")}
          >
            {t.study_abroad} {freeBadge}
          </a>
          {dropdown("sch", t.schools, schools, 300, 1, "hp-nav__i--sch")}
          {dropdown(
            "abt",
            <>
              <span className="hp-lbl-full">{t.about_yolutap}</span>
              <span className="hp-lbl-short">{HEADER_ABOUT_SHORT[lang]}</span>
            </>,
            about,
            280,
            1,
            "hp-nav__i--abt",
          )}
          <a
            className="hp-nav__link hp-nav__i--prices"
            href={pricesHref}
            aria-current={cur("pricing")}
          >
            {t.prices}
          </a>
        </nav>
        <div className="hp-header__right">
          {/* Пилюля регистрации репетитора — на всех страницах (ТЗ «два пути»,
              09.10.2026). На телефоне (<690) она уходит в бургер вторым пунктом,
              в строке остаются логотип, тест, язык и бургер (решение владельца
              11.10.2026) — брейкпоинты в site-chrome.css. */}
          {tutor ? (
            <a href={TUTORS_ME_HREF[lang]} className="hp-header__join" data-cta="header_tutor_cabinet">
              {MY_CABINET_LABEL[lang]}
            </a>
          ) : (
            <a href={TUTORS_JOIN_HREF[lang]} className="hp-header__join" data-cta="header_tutor_join">
              {TUTORS_SIGNUP_LABEL[lang]}
            </a>
          )}
          <div className="hp-lang" role="group" aria-label={t.language}>
            {langLinks()}
          </div>
          <div className="hp-dd hp-login" hidden={tutor}>
            <button type="button" className="hp-dd__btn" data-dd="login" aria-expanded="false">
              {k.login.label} <Chevron />
            </button>
            <div className="hp-dd__panel hp-dd__panel--right" data-dd-panel="login" hidden style={{ width: 264 }}>
              {loginItems.map((it) => (
                <a key={it.href} className="hp-dd__item" href={it.href}>
                  <b>{it.title}</b>
                  <span>{it.desc}</span>
                </a>
              ))}
            </div>
          </div>
          {/* Телефон ≤ 480 (ТЗ владельца 25.10.2026): язык — три кнопки прямо в
              шапке, без выпадающего меню; «Giriş» — выбор из двух кабинетов.
              «Testdən keç» и «AZ ▾» на телефоне скрыты (site-chrome.css). */}
          <div className="hp-plang" role="group" aria-label={t.language} data-lang-switch="phone">
            {langLinks()}
          </div>
          {/* Нативный <details>: открывается и без JS (на iPhone кнопка на
              обработчике шапки не реагировала, 25.10.2026). initSiteHeader только
              закрывает его кликом мимо и по Esc. */}
          <details className="hp-plogin" data-plogin="" hidden={tutor}>
            <summary className="hp-dd__btn hp-plogin__btn">{k.login.label}</summary>
            <div className="hp-dd__panel hp-dd__panel--right hp-plogin__panel">
              {phoneLoginItems.map((it) => (
                <a key={it.href} className="hp-dd__item" href={it.href} data-cta={it.cta}>
                  <b>{it.title}</b>
                </a>
              ))}
            </div>
          </details>
          <a href={`${start}&pkg=basic`} className="hp-btn hp-header__cta" data-cta="header_start">
            {t.take_the_test_2}
          </a>
          {/* Компактный переключатель языка — ниже 860, где полный уходит из
              строки. Тот же механизм выпадающих меню (data-dd): Esc и клик
              мимо закрывают, фокус возвращается на кнопку. */}
          <div className="hp-dd hp-lang-mini" data-lang-switch="header">
            <button
              type="button"
              className="hp-dd__btn hp-lang-mini__btn"
              data-dd="lang"
              aria-expanded="false"
              aria-haspopup="true"
              aria-controls="hp-lang-mini"
              aria-label={`${LANG_SWITCH_LABEL}: ${lang.toUpperCase()}`}
            >
              {lang.toUpperCase()} <Chevron />
            </button>
            <div
              id="hp-lang-mini"
              className="hp-dd__panel hp-dd__panel--right hp-lang-mini__panel"
              data-dd-panel="lang"
              role="group"
              aria-label={LANG_SWITCH_LABEL}
              hidden
            >
              {langs.map((x) => (
                <a key={x.lang} href={x.href} hrefLang={x.lang} lang={x.lang} data-lang={x.lang} aria-current={x.current ? "true" : undefined}>
                  <b>{x.lang.toUpperCase()}</b> {LANG_NAME[x.lang]}
                </a>
              ))}
            </div>
          </div>
          <button type="button" className="hp-burger" data-burger="" aria-expanded="false" aria-controls="hp-menu" aria-label={t.menu}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
        </div>
      </div>
      <nav id="hp-menu" className="hp-menu" data-menu="" aria-label={t.menu} hidden>
        <div className="hp-wrap hp-menu__in">
          {/* Первая строка меню — язык, вторая — регистрация репетитора (на
              телефоне пилюли в строке шапки нет). */}
          <div className="hp-menu__lang" role="group" aria-label={LANG_SWITCH_LABEL} data-lang-switch="menu">
            {langLinks()}
          </div>
          {tutor ? (
            <a href={TUTORS_ME_HREF[lang]} className="hp-menu__join" data-cta="menu_tutor_cabinet">
              {MY_CABINET_LABEL[lang]}
            </a>
          ) : (
            <a href={TUTORS_JOIN_HREF[lang]} className="hp-menu__join" data-cta="menu_tutor_join">
              {TUTORS_SIGNUP_LABEL[lang]}
            </a>
          )}
          <div className="hp-menu__group">
            <span className="hp-menu__label">{t.families}</span>
            {families.map(menuItem)}
          </div>
          <a href={STUDY_ABROAD_HREF[lang]} aria-current={cur("abroad")} style={{ borderBottom: "1px solid var(--color-line)" }}>
            {t.study_abroad} {freeBadge}
          </a>
          <div className="hp-menu__group">
            <span className="hp-menu__label">{t.schools}</span>
            {schools.map(menuItem)}
          </div>
          <div className="hp-menu__group">
            <span className="hp-menu__label">{t.about_yolutap}</span>
            {about.map(menuItem)}
          </div>
          <div className="hp-menu__group" hidden={tutor}>
            <span className="hp-menu__label">{k.login.label}</span>
            {loginItems.map((it) => (
              <a key={it.href} href={it.href}>
                {it.title}
              </a>
            ))}
          </div>
          <a href={pricesHref} aria-current={cur("pricing")} style={{ borderBottom: "1px solid var(--color-line)" }}>
            {t.prices}
          </a>
        </div>
      </nav>
    </header>
  );
}

export function SiteFooter({
  lang,
  active,
  home = false,
  note,
  langHref = HOME_PATH,
}: {
  lang: SiteLang;
  active?: SiteSection;
  home?: boolean;
  /** Строка страницы под подвалом — например, атрибуция O*NET на карте профессий. */
  note?: string;
  /** Та же страница на других языках (как у шапки); по умолчанию — главные. */
  langHref?: Record<SiteLang, string>;
}) {
  const t = COPY[lang];
  const k = kept[lang];
  const pre = PRE[lang];
  const start = `/start?lang=${lang}`;
  const cur = (s: SiteSection) => (s === active ? ("page" as const) : undefined);
  return (
    <footer className="hp hp-footer" data-site-footer="">
      <div className="hp-wrap hp-footer__in">
        <div className="hp-footer__col">
          <b>{t.families}</b>
          <a href={`${start}&pkg=basic`}>{t.career_profile}</a>
          <a href={FREE_TEST_PATH[lang]}>{FREE_TEST_LABEL[lang]}</a>
          <a href={`${start}&pkg=premium`}>{t.coach_session}</a>
          <a href={STUDY_ABROAD_HREF[lang]} aria-current={cur("abroad")}>{t.study_abroad}</a>
          <a href={`${pre}/coaches`} aria-current={cur("coaches")}>{t.coaches}</a>
          <a href={`${pre}/careers`} aria-current={cur("careers")}>{t.careers}</a>
          <a href={TUTORS_MAP_HREF[lang]} aria-current={cur("tutors")}>{TUTORS_MAP_ITEM[lang].title}</a>
        </div>
        <div className="hp-footer__col">
          <b>{t.schools}</b>
          <a href={SCHOOLS_PATH[lang]} aria-current={cur("schools")}>{t.career_guidance_for_classes}</a>
          <a href={SCHOOLS_PATH[lang]}>{t.school_climate}</a>
        </div>
        <div className="hp-footer__col">
          <b>Yolutap</b>
          <a href={`${pre}/about`} aria-current={cur("about")}>{t.about_us}</a>
          <a href={BLOG_PATH[lang]}>{BLOG_COPY[lang].footer}</a>
          <a href={METHOD_PATH[lang]} aria-current={cur("methodology")}>{t.methodology_2}</a>
          <a href={TUTORS_JOIN_HREF[lang]}>{TUTORS_JOIN_LABEL[lang]}</a>
          <a href={`${pre}/privacy`} aria-current={cur("privacy")}>{t.privacy}</a>
          <a href={`${pre}/terms`} aria-current={cur("terms")}>{TERMS[lang]}</a>
          <a href="mailto:support@yolutap.com">support@yolutap.com</a>
        </div>
        <div className="hp-footer__meta">
          <div className="hp-footer__lang" role="group" aria-label={LANG_SWITCH_LABEL} data-lang-switch="footer">
            {siteLangLinks(lang, langHref).map((x) => (
              <a key={x.lang} href={x.href} hrefLang={x.lang} data-lang={x.lang} aria-current={x.current ? "true" : undefined}>
                {x.lang.toUpperCase()}
              </a>
            ))}
          </div>
          {home && <span>{t.photos_european_azerbaijan_school_used_with_perm}</span>}
          <span>{t["2026_yolutap_llc_baku"]}</span>
        </div>
      </div>
      <div className="hp-wrap hp-footer__legal">
        {note && <p className="hp-footer__note">{note}</p>}
        {k.legal}
      </div>
    </footer>
  );
}
